import { Severity } from 'Data/ViewModels/Core/types';
import { DateTimeDataFormat, FormatSpecifier, getTimeInputDataForPattern } from 'Lib/Format';
import classNames from 'classnames';
import moment from 'moment';
import React, { useState } from 'react';
import { withInjectedProps } from '../InjectedPropsContext';

export type ITimeInputProps = Pick<
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'disabled' | 'id' | 'name' | 'onChange' | 'onKeyDown'
> & {
  focusRef?: React.Ref<HTMLInputElement>;
  severity?: Severity;
  value?: any;
  dataFormat?: DateTimeDataFormat;
  format?: FormatSpecifier;
  min?: string;
  max?: string;
};

const TimeInputBase = ({
  disabled,
  id,
  focusRef,
  name,
  onChange,
  onKeyDown,
  severity,
  value,
  dataFormat = DateTimeDataFormat.Time,
  format,
  min,
  max,
}: ITimeInputProps) => {
  //When we have linked date and time inputs, we need better control over what is written back to the underlying model
  //state. This is essentially to delay clearing the underlying value when the input value is null due to the entered
  //date being invalid.
  const [inputValue, setInputValue] = useState<any>();
  const [dirty, setDirty] = useState<boolean>(false);

  const formatInfo = getTimeInputDataForPattern(format?.pattern ?? moment.HTML5_FMT.TIME);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.defaultPrevented) {
      invokeOnChange(event, true);
    }
    onKeyDown?.(event);
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => invokeOnChange(event, false);
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => invokeOnChange(event, true);

  const invokeOnChange = (event: any, submitValues: boolean) => {
    var newValue = event.target.value?.length > 0 ? event.target.value : null;
    setInputValue(newValue);

    // In some browsers (Firefox), clicking the clear 'x' doesn't focus or blur the input, so
    // if if the value was cleared, and we don't currently have focus on the input, submit the
    // change immediately.
    const submitValuesBecauseCleared =
      newValue === null &&
      event.target !== document.activeElement &&
      (event.target as HTMLInputElement).checkValidity();

    // Delay writing to the model if this is part of a pair of linked date and time inputs, and value is only
    // temporarily cleared (we always need to write to the model before submitting values to the server).
    if (
      dataFormat === DateTimeDataFormat.UnspecifiedLocalDateTime &&
      newValue === null &&
      !(submitValues || submitValuesBecauseCleared)
    ) {
      setDirty(true);
      return;
    }

    if (dataFormat === DateTimeDataFormat.UnspecifiedLocalDateTime) {
      // The value on the model is an "unspecified local date and time". When value in the time input is set, we want to
      // modify only the time part of the value, and leave the date intact.
      var oldDateTime = moment(value, 'YYYY-MM-DDTHH:mm:ss.SSS', true);
      if (!oldDateTime.isValid()) {
        oldDateTime = moment().local();
      }
      var newTime = moment(newValue, formatInfo.html5TimePattern, true);
      if (newTime.isValid()) {
        newValue = oldDateTime
          .hour(newTime.hour())
          .minute(newTime.minute())
          .second(newTime.second())
          .millisecond(newTime.millisecond())
          .format('YYYY-MM-DDTHH:mm:ss.SSS');
      }
    }
    if (newValue == null && (submitValues || submitValuesBecauseCleared)) {
      //Clear partially entered value to avoid possible confusion with a value being displayed, but not being saved
      event.target.value = '';
    }

    event.data = { name, value: newValue, submitValues: submitValues || submitValuesBecauseCleared };
    onChange?.(event);
    setDirty(false);
  };

  // Undefined needs to pass through to switch between a controlled vs uncontrolled component.
  // When this is a linked date/time input, we are extracting only the time portion of the underlying
  // value on the model.
  const inputValueFromModel =
    value === undefined || value === null
      ? value
      : moment(value, ['YYYY-MM-DDTHH:mm:ss.SSS', 'YYYY-MM-DDTHH:mm:ss', 'HH:mm:ss', 'HH:mm:ss.SSS']).format(
          formatInfo.html5TimePattern
        );

  // Sync value to display from props unless user has made local changes
  // Local changes flagged through handleChange and then synced through handleBlur
  if (value !== undefined && !dirty && inputValue !== inputValueFromModel) {
    setInputValue(inputValueFromModel);
  }

  const cx = classNames('TimeInput', { [`Input-${severity}`]: !!severity });
  return (
    <input
      className={cx}
      data-testid={id}
      disabled={disabled}
      id={id}
      name={name}
      onBlur={handleBlur}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      ref={focusRef}
      type="time"
      step={formatInfo.step}
      min={min}
      max={max}
      value={value === undefined ? undefined : inputValue ?? ''}
    />
  );
};

const TimeInput = withInjectedProps(TimeInputBase);

export { TimeInput, TimeInputBase };
export default TimeInput;
